/**
 * Dashboard Persona Presets
 *
 * Defines 3 user personas for internal review: Newcomer, Explorer, Veteran.
 * Each preset injects realistic store data so reviewers can see how the
 * adaptive dashboard renders for different user maturity levels.
 */

import { LEVEL_DEFINITIONS } from '../../types/gamification.types'
import { useGamificationStore } from '../../store/useGamificationStore'
import { useChallengeStore } from '../../store/useChallengeStore'
import { useCommunityStore } from '../../store/useCommunityStore'
import { useDebugPanelStore } from '../../store/useDebugPanelStore'
import type { DashboardTier } from '../../pages/dashboard/useDashboardTier'
import type { UserBadge, XPEvent, LeaderboardEntry, UserChallenge, Community } from '../../types'

// ── Persona definitions ──

interface DashboardPersona {
  key: DashboardTier
  label: string
  gamification: {
    totalXp: number
    currentLevel: typeof LEVEL_DEFINITIONS[number]
    xpProgress: { progress: number; toNext: number }
    currentStreak: number
    userBadges: UserBadge[]
    recentXpEvents: XPEvent[]
    leaderboard: LeaderboardEntry[]
  }
  challenges: {
    myChallenges: UserChallenge[]
    myChallengeStats: { invitations: number; fromOthers: number; inProgress: number; inReview: number; completed: number; unverified: number; declined: number }
  }
  communities: {
    myCommunities: Community[]
  }
}

const now = new Date().toISOString()
const daysAgo = (d: number) => new Date(Date.now() - d * 86400000).toISOString()

// ── Shared mock data factories ──

function makeBadge(id: string, name: string, earnedDaysAgo: number): UserBadge {
  return {
    id: `ub-${id}`,
    userId: 'demo-user-001',
    badgeId: `badge-${id}`,
    badge: { id: `badge-${id}`, name, description: `Earned: ${name}`, category: 'milestone', rarity: 'common', criteria: { type: 'challenge_count', value: 1 }, xpBonus: 10, isActive: true, createdAt: now, updatedAt: now },
    earnedAt: daysAgo(earnedDaysAgo),
    createdAt: daysAgo(earnedDaysAgo),
    updatedAt: daysAgo(earnedDaysAgo),
  }
}

function makeXpEvent(id: string, type: string, amount: number, daysBack: number, title?: string): XPEvent {
  return {
    id: `xp-${id}`,
    userId: 'demo-user-001',
    eventType: type,
    amount,
    metadata: title ? { challengeTitle: title } : undefined,
    createdAt: daysAgo(daysBack),
    updatedAt: daysAgo(daysBack),
  }
}

function makeChallenge(id: string, title: string, status: string, daysBack: number): UserChallenge {
  return {
    id: `uc-${id}`,
    userId: 'demo-user-001',
    challengeId: `challenge-${id}`,
    status: status as UserChallenge['status'],
    startedAt: daysAgo(daysBack),
    challenge: {
      id: `challenge-${id}`,
      title,
      shortDescription: `A challenge: ${title}`,
      description: title,
      source: 'platform',
      categoryIds: ['cat-outdoor'],
      difficulty: 'beginner',
      estimatedDuration: '2-3 hours',
      soloGroup: 'both',
      isFree: true,
      hasMilestones: false,
      baseXp: 75,
      status: 'approved',
      completionCount: 10,
      trackRecordCount: 5,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
    },
    createdAt: daysAgo(daysBack),
    updatedAt: daysAgo(daysBack),
  } as UserChallenge
}

function makeCommunity(id: string, name: string, members: number): Community {
  return {
    id: `comm-${id}`,
    name,
    description: `A community for ${name.toLowerCase()}.`,
    slug: name.toLowerCase().replace(/\s+/g, '-'),
    type: 'interest_hobby',
    ownerUserId: 'demo-user-001',
    visibility: 'public',
    allowMemberPosts: true,
    requirePostApproval: false,
    isAutoMembership: false,
    memberCount: members,
    postCount: members * 3,
    createdAt: now,
    updatedAt: now,
  }
}

function makeLeaderboardEntry(rank: number, name: string, xp: number): LeaderboardEntry {
  return {
    id: `lb-${rank}`,
    rank,
    userId: `user-${rank}`,
    displayName: name,
    totalXp: xp,
    challengesCompleted: Math.floor(xp / 100),
    currentStreak: Math.floor(xp / 500),
    badgeCount: Math.floor(xp / 300),
  }
}

// ── The 3 personas ──

export const DASHBOARD_PERSONAS: DashboardPersona[] = [
  {
    key: 'newcomer',
    label: 'New User',
    gamification: {
      totalXp: 25,
      currentLevel: LEVEL_DEFINITIONS[0], // Level 1: Newcomer
      xpProgress: { progress: 25, toNext: 75 },
      currentStreak: 0,
      userBadges: [],
      recentXpEvents: [],
      leaderboard: [],
    },
    challenges: {
      myChallenges: [makeChallenge('001', 'Cook a Meal from Another Culture', 'in_progress', 2)],
      myChallengeStats: { invitations: 0, fromOthers: 0, inProgress: 1, inReview: 0, completed: 0, unverified: 0, declined: 0 },
    },
    communities: {
      myCommunities: [],
    },
  },
  {
    key: 'explorer',
    label: 'Active User',
    gamification: {
      totalXp: 650,
      currentLevel: LEVEL_DEFINITIONS[2], // Level 3: Explorer
      xpProgress: { progress: 300, toNext: 200 },
      currentStreak: 4,
      userBadges: [
        makeBadge('001', 'First Steps', 30),
        makeBadge('003', 'Nature Lover', 14),
        makeBadge('006', 'Social Butterfly', 7),
      ],
      recentXpEvents: [
        makeXpEvent('1', 'challenge_completed', 100, 1, 'Build a Birdhouse'),
        makeXpEvent('2', 'badge_earned', 25, 1, 'Nature Lover'),
        makeXpEvent('3', 'streak_bonus', 15, 2),
        makeXpEvent('4', 'track_entry_added', 5, 3, 'Cook a Meal'),
        makeXpEvent('5', 'first_of_day', 5, 4),
      ],
      leaderboard: [
        makeLeaderboardEntry(1, 'TopExplorer', 2400),
        makeLeaderboardEntry(2, 'TrailRunner', 1800),
        makeLeaderboardEntry(3, 'CuriousCat', 1200),
        makeLeaderboardEntry(4, 'Demo User', 650),
        makeLeaderboardEntry(5, 'NoviceHiker', 400),
      ],
    },
    challenges: {
      myChallenges: [
        makeChallenge('002', 'Cook a Meal from Another Culture', 'in_progress', 3),
        makeChallenge('005', 'Plant a Garden', 'in_progress', 7),
        makeChallenge('008', 'Learn Basic Origami', 'in_progress', 1),
      ],
      myChallengeStats: { invitations: 1, fromOthers: 0, inProgress: 3, inReview: 1, completed: 5, unverified: 0, declined: 0 },
    },
    communities: {
      myCommunities: [
        makeCommunity('001', 'Weekend Adventurers', 42),
        makeCommunity('002', 'Cooking Enthusiasts', 28),
      ],
    },
  },
  {
    key: 'veteran',
    label: 'Power User',
    gamification: {
      totalXp: 8500,
      currentLevel: LEVEL_DEFINITIONS[7], // Level 8: Voyager
      xpProgress: { progress: 1900, toNext: 1600 },
      currentStreak: 14,
      userBadges: [
        makeBadge('001', 'First Steps', 180),
        makeBadge('002', 'Trailblazer', 150),
        makeBadge('003', 'Nature Lover', 120),
        makeBadge('004', 'Master Builder', 90),
        makeBadge('005', 'Consistency King', 60),
        makeBadge('006', 'Social Butterfly', 45),
        makeBadge('007', 'Helping Hand', 30),
        makeBadge('008', 'Renaissance Explorer', 14),
      ],
      recentXpEvents: [
        makeXpEvent('1', 'challenge_completed', 150, 0, 'Advanced Woodworking'),
        makeXpEvent('2', 'badge_earned', 100, 0, 'Renaissance Explorer'),
        makeXpEvent('3', 'streak_bonus', 30, 1),
        makeXpEvent('4', 'challenge_completed', 100, 2, 'Trail Running 10K'),
        makeXpEvent('5', 'verification_given', 10, 2),
        makeXpEvent('6', 'community_post', 3, 3),
        makeXpEvent('7', 'track_entry_added', 5, 4, 'Sourdough Bread'),
        makeXpEvent('8', 'first_of_day', 5, 5),
      ],
      leaderboard: [
        makeLeaderboardEntry(1, 'LegendaryExplorer', 15000),
        makeLeaderboardEntry(2, 'MasterCrafter', 12000),
        makeLeaderboardEntry(3, 'Demo User', 8500),
        makeLeaderboardEntry(4, 'TrailBlazerPro', 7200),
        makeLeaderboardEntry(5, 'NatureLover99', 6100),
        makeLeaderboardEntry(6, 'CraftKing', 5400),
        makeLeaderboardEntry(7, 'AdventureSeeker', 4800),
        makeLeaderboardEntry(8, 'CuriousMind', 3900),
        makeLeaderboardEntry(9, 'PathfinderX', 3200),
        makeLeaderboardEntry(10, 'ExplorerJr', 2500),
      ],
    },
    challenges: {
      myChallenges: [
        makeChallenge('010', 'Advanced Woodworking', 'in_progress', 5),
        makeChallenge('012', 'Sourdough Bread Mastery', 'in_progress', 10),
        makeChallenge('015', 'Watercolor Landscapes', 'in_progress', 3),
        makeChallenge('018', 'Trail Running 10K', 'in_progress', 8),
        makeChallenge('020', 'Build a Solar Oven', 'in_progress', 1),
      ],
      myChallengeStats: { invitations: 2, fromOthers: 3, inProgress: 5, inReview: 2, completed: 32, unverified: 1, declined: 0 },
    },
    communities: {
      myCommunities: [
        makeCommunity('001', 'Weekend Adventurers', 42),
        makeCommunity('002', 'Cooking Enthusiasts', 128),
        makeCommunity('003', 'Makers & Builders', 67),
        makeCommunity('004', 'Trail Runners Club', 35),
      ],
    },
  },
]

// ── Apply function ──

export function applyDashboardPersona(key: DashboardTier) {
  const persona = DASHBOARD_PERSONAS.find((p) => p.key === key)
  if (!persona) return

  useGamificationStore.setState(persona.gamification)
  useChallengeStore.setState(persona.challenges)
  useCommunityStore.setState(persona.communities)
  useDebugPanelStore.setState({ dashboardTierOverride: persona.key })
}

export function clearDashboardPersona() {
  useDebugPanelStore.setState({ dashboardTierOverride: null })
}
